
import {inject, Injectable} from '@angular/core';
import {OtpStore} from '../state/otp.store';
import {OtpQuery} from '../state/otp.query';
import {OtpApiService} from './otp-api.service';
import {Router} from '@angular/router';
import {OtpPurpose, ResetPasswordRequest} from '../../models/otp.models';
import {catchError, Observable, tap, throwError} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class OtpService {
  private otpStore = inject(OtpStore);
  private otpQuery = inject(OtpQuery);
  private otpApi = inject(OtpApiService);
  private router = inject(Router);

  generateOtp(email: string, purpose: OtpPurpose): Observable<any> {
    this.otpStore.setOtpLoading(true);
    this.otpStore.setEmail(email);

    return this.otpApi.generateOtp({ email, purpose }).pipe(
      tap(response => {
        this.otpStore.setExpiresIn(response.expiresInMinutes);
        this.otpStore.setOtpLoading(false);
      }),
      catchError(error => {
        this.otpStore.setOtpError(error.error?.message || 'Failed to generate OTP');
        return throwError(() => error);
      })
    );
  }

  verifyOtp(email: string, otpCode: string, purpose: OtpPurpose): Observable<any> {
    this.otpStore.setOtpLoading(true);

    return this.otpApi.verifyOtp({ email, otpCode, purpose }).pipe(
      tap(response => {
        this.otpStore.setOtpVerified(response.valid);
        this.otpStore.setOtpLoading(false);
      }),
      catchError(error => {
        this.otpStore.setOtpError(error.error?.message || 'Invalid OTP');
        return throwError(() => error);
      })
    );
  }

  resendOtp(email: string, purpose: OtpPurpose): Observable<any> {
    this.otpStore.setOtpLoading(true);

    return this.otpApi.resendOtp({ email, purpose }).pipe(
      tap(response => {
        this.otpStore.setExpiresIn(response.expiresInMinutes);
        this.otpStore.setOtpLoading(false);
      }),
      catchError(error => {
        this.otpStore.setOtpError(error.error?.message || 'Failed to resend OTP');
        return throwError(() => error);
      })
    );
  }

  resetPassword(request: ResetPasswordRequest): Observable<void> {
    this.otpStore.setOtpLoading(true);

    return this.otpApi.resetPassword(request).pipe(
      tap(() => {
        this.otpStore.setOtpLoading(false);
        this.otpStore.clearOtpState();
      }),
      catchError(error => {
        this.otpStore.setOtpError(error.error?.message || 'Failed to reset password');
        return throwError(() => error);
      })
    );
  }

  clearState() {
    this.otpStore.clearOtpState();
  }
}
